import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection as firestoreCollection, doc, getDoc, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community';
import Select from 'react-select';
import {
  Typography, TextField, MenuItem, Alert, Box, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import BulkUploadDialog from './BulkUploadDialog';

const listRegistrantsForEdit = httpsCallable(functions, 'listRegistrantsForEdit');
const updateRegistrant = httpsCallable(functions, 'updateRegistrant');
const cancelRegistrant = httpsCallable(functions, 'cancelRegistrant');
const refundRegistrant = httpsCallable(functions, 'refundRegistrant');
const deleteRegistrant = httpsCallable(functions, 'deleteRegistrant');

// sp26 predates the unified `registrants` collection - see AdminPage.js and
// functions/index.js for the same legacy/new distinction used elsewhere.
const LEGACY_VIEW_OPTIONS = [{ id: 'sp26', mode: 'legacy', label: 'sp26 (legacy)' }];

const GENDER_OPTIONS = ['M', 'F'];
const STATUS_OPTIONS = [
  'High School Senior', 'Freshman', 'Sophomore', 'Junior', 'Senior', 'Grad Student',
  'Recent College Graduate', 'FT', 'Serving One', 'Other',
];
// Fallback used for the legacy (pre-conferences-collection) view, which has
// no tshirt.sizes config doc to read sizes from.
const DEFAULT_SHIRT_SIZE_OPTIONS = ['none', 'S', 'M', 'L', 'XL', 'XXL'];

// Fields the search bar matches against, case-insensitive substring.
const SEARCH_FIELDS = ['firstName', 'lastName', 'email', 'phoneNumber', 'school', 'city', 'services', 'allergies'];

// amountPaid/refundAmount/financialSupportAmount all need Number() coercion
// rather than a typeof check, since several things can leave them stored as
// strings: PayPal returns captured amounts as strings (e.g. "17.00"), and
// ag-Grid's own default cell editor always returns a string on save (see
// handleCellValueChanged/updateRegistrant). A typeof check alone either
// renders every real value as "—", or - worse, for an ag-Grid-inferred
// "number" column like financialSupportAmount - as the literal text
// "Invalid Number".
const currencyFormatter = (params) => {
  const n = Number(params.value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
};
const yesNoFormatter = (params) => (params.value === true ? 'Yes' : params.value === false ? 'No' : '—');
// createdAt comes back as an ISO string (see listRegistrantsForEdit), not a
// Firestore Timestamp - formatted here for display, sorted/filtered on the
// original ISO string underneath so chronological order still works.
const dateTimeFormatter = (params) => {
  if (!params.value) return '—';
  const d = new Date(params.value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

function ActionsCellRenderer(params) {
  const { onCancelClick, onRefundClick, onDeleteClick, canRefund } = params.context;
  const row = params.data;
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
      <Button size="small" variant="outlined" color={row.canceled ? 'success' : 'warning'} onClick={() => onCancelClick(row)}>
        {row.canceled ? 'Reinstate' : 'Cancel'}
      </Button>
      {canRefund && (
        <>
          <Button size="small" variant="outlined" color="error" disabled={row.refunded || !(row.amountPaid > 0)} onClick={() => onRefundClick(row)}>
            {row.refunded ? 'Refunded' : 'Refund'}
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={() => onDeleteClick(row)}>
            Delete
          </Button>
        </>
      )}
    </Stack>
  );
}

// Editing surface for EDITOR/ADMIN/OWNER - intentionally leaves the actual
// payment/order fields (orderID, products) out of what's editable: those
// should only ever reflect the real PayPal transaction (see
// EDITABLE_REGISTRANT_FIELDS in functions/index.js, which the backend
// enforces regardless of what this UI sends). amountPaid/canceled/refunded
// are shown read-only for reference, changed only via the Cancel/Refund/
// Delete buttons below - never by editing a cell directly. `canRefund` gates
// both Refund and Delete, since the backend restricts both to ADMIN/OWNER
// (money movement and a permanent, unrecoverable action, respectively).
export default function EditRegistrantsPanel({ canRefund, canBulkUpload }) {
  const [viewOptions, setViewOptions] = useState(LEGACY_VIEW_OPTIONS);
  const [viewingId, setViewingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmountInput, setRefundAmountInput] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [localityFilter, setLocalityFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [shirtSizeOptions, setShirtSizeOptions] = useState(DEFAULT_SHIRT_SIZE_OPTIONS);
  const [gridApi, setGridApi] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(firestoreCollection(db, 'conferences')),
        ]);
        const newOptions = confsSnap.docs.map((d) => ({ id: d.id, mode: 'new', label: d.id }));
        const options = [...newOptions, ...LEGACY_VIEW_OPTIONS];
        setViewOptions(options);
        const active = activeSnap.data()?.conferenceId;
        setViewingId(active || options[0]?.id || null);
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setViewOptions(LEGACY_VIEW_OPTIONS);
        setViewingId(LEGACY_VIEW_OPTIONS[0].id);
      }
    })();
  }, []);

  const currentOption = viewOptions.find((o) => o.id === viewingId);

  // Pulled out of the effect below so BulkUploadDialog can trigger the same
  // refresh after a successful upload, without duplicating the fetch logic.
  const loadRows = useCallback(async (resetFilters) => {
    if (!viewingId) return;
    setLoading(true);
    setMessage(null);
    try {
      const params = currentOption?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
      const res = await listRegistrantsForEdit(params);
      // services/allergies come back as arrays (see ServicesForm.js/
      // InformationForm.js) - shown/edited here as a comma-separated string,
      // converted back on save.
      const items = (res.data?.items || []).map((item) => ({
        ...item,
        services: Array.isArray(item.services) ? item.services.join(', ') : (item.services || ''),
        allergies: Array.isArray(item.allergies) ? item.allergies.join(', ') : (item.allergies || ''),
      }));
      setRows(items);
      if (resetFilters) {
        setLocalityFilter([]);
        setSearchQuery('');
      }
    } catch (error) {
      console.error('Failed to load registrants for editing:', error);
      setRows([]);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, currentOption]);

  useEffect(() => {
    loadRows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, viewOptions]);

  // Populates the T-shirt size column's dropdown from this conference's own
  // configured sizes (conferences/{id}.tshirt.sizes) so it always matches
  // what registrants were actually offered - see ConferenceSettingsPanel.
  useEffect(() => {
    if (!viewingId || currentOption?.mode === 'legacy') {
      setShirtSizeOptions(DEFAULT_SHIRT_SIZE_OPTIONS);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'conferences', viewingId));
        const sizes = snap.data()?.tshirt?.sizes || [];
        setShirtSizeOptions(['none', ...sizes]);
      } catch (error) {
        console.error('Failed to load t-shirt sizes for conference:', error);
        setShirtSizeOptions(DEFAULT_SHIRT_SIZE_OPTIONS);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, currentOption]);

  const handleExportCsv = useCallback(() => {
    gridApi?.exportDataAsCsv({ fileName: `registrants-${viewingId || 'export'}.csv` });
  }, [gridApi, viewingId]);

  const handleCellValueChanged = useCallback(async (event) => {
    const field = event.colDef.field;
    if (event.oldValue === event.newValue) return;
    // services/allergies are edited/displayed as a comma-separated string
    // here, but stored (and read elsewhere) as an array.
    const valueToSave = (field === 'services' || field === 'allergies')
      ? String(event.newValue || '').split(',').map((s) => s.trim()).filter(Boolean)
      : field === 'financialSupportAmount'
        // ag-Grid's default cell editor always returns a string - without
        // this, saving this cell re-introduces the "Invalid Number" display
        // bug (see currencyFormatter/updateRegistrant above).
        ? (Number.isFinite(Number(event.newValue)) ? Number(event.newValue) : 0)
        : event.newValue;
    try {
      const params = {
        id: event.data.id,
        updates: { [field]: valueToSave },
      };
      if (currentOption?.mode === 'legacy') params.collection = viewingId;
      await updateRegistrant(params);
      setMessage({ type: 'success', text: `Saved ${field} for ${event.data.firstName || ''} ${event.data.lastName || ''}.` });
    } catch (error) {
      console.error('Failed to save edit:', error);
      setMessage({ type: 'error', text: `Failed to save ${field}: ${error.message}` });
      // Revert the cell in the grid since the write failed.
      event.node.setDataValue(field, event.oldValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption, viewingId]);

  const handleCancelClick = useCallback(async (row) => {
    const nextCanceled = !row.canceled;
    try {
      const params = { id: row.id, canceled: nextCanceled };
      if (currentOption?.mode === 'legacy') params.collection = viewingId;
      await cancelRegistrant(params);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, canceled: nextCanceled } : r)));
      setMessage({ type: 'success', text: `${nextCanceled ? 'Canceled' : 'Reinstated'} ${row.firstName || ''} ${row.lastName || ''}.` });
    } catch (error) {
      console.error('Failed to update cancellation status:', error);
      setMessage({ type: 'error', text: error.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption, viewingId]);

  const handleRefundClick = useCallback((row) => {
    setRefundTarget(row);
    const paid = Number(row.amountPaid);
    setRefundAmountInput(Number.isFinite(paid) ? paid.toFixed(2) : '');
  }, []);

  async function confirmRefund() {
    if (!refundTarget) return;
    setRefundBusy(true);
    setMessage(null);
    try {
      const params = { id: refundTarget.id, amount: Number(refundAmountInput) };
      if (currentOption?.mode === 'legacy') params.collection = viewingId;
      await refundRegistrant(params);
      setRows((prev) => prev.map((r) => (r.id === refundTarget.id ? { ...r, refunded: true, refundAmount: Number(refundAmountInput) } : r)));
      setMessage({ type: 'success', text: `Refunded $${Number(refundAmountInput).toFixed(2)} for ${refundTarget.firstName || ''} ${refundTarget.lastName || ''}.` });
      setRefundTarget(null);
    } catch (error) {
      console.error('Refund failed:', error);
      setMessage({ type: 'error', text: `Refund failed: ${error.message}` });
    } finally {
      setRefundBusy(false);
    }
  }

  const handleDeleteClick = useCallback((row) => {
    setDeleteTarget(row);
  }, []);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setMessage(null);
    try {
      const params = { id: deleteTarget.id };
      if (currentOption?.mode === 'legacy') params.collection = viewingId;
      await deleteRegistrant(params);
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setMessage({ type: 'success', text: `Deleted ${deleteTarget.firstName || ''} ${deleteTarget.lastName || ''}.` });
      setDeleteTarget(null);
    } catch (error) {
      console.error('Delete failed:', error);
      setMessage({ type: 'error', text: `Delete failed: ${error.message}` });
    } finally {
      setDeleteBusy(false);
    }
  }

  const colDefs = useMemo(() => [
    {
      headerName: 'Actions', pinned: 'left', width: canRefund ? 320 : 120,
      sortable: false, filter: false, cellRenderer: ActionsCellRenderer,
    },

    // Identity / contact
    { field: 'firstName', headerName: 'First name', editable: true },
    { field: 'lastName', headerName: 'Last name', editable: true },
    { field: 'email', editable: true, width: 200 },
    { field: 'phoneNumber', headerName: 'Phone', editable: true, width: 140 },
    { field: 'gender', editable: true, width: 100, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: GENDER_OPTIONS } },
    { field: 'status', editable: true, width: 170, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: STATUS_OPTIONS } },
    { field: 'school', editable: true, width: 180 },
    { field: 'city', editable: true, width: 130 },
    { field: 'locality', editable: true, width: 130 },

    // Registration & payment status
    { field: 'participation', editable: false, width: 130 },
    { field: 'amountPaid', headerName: 'Amount paid', editable: false, width: 120, valueFormatter: currencyFormatter },
    { field: 'canceled', headerName: 'Canceled?', editable: false, width: 110, valueFormatter: yesNoFormatter },
    { field: 'refunded', headerName: 'Refunded?', editable: false, width: 110, valueFormatter: yesNoFormatter },
    { field: 'refundAmount', headerName: 'Refund amount', editable: false, width: 130, valueFormatter: currencyFormatter },

    // Waivers
    { field: 'signedWaiver', headerName: 'Liability waiver', editable: true, width: 130, cellEditor: 'agCheckboxCellEditor', cellRenderer: 'agCheckboxCellRenderer' },
    { field: 'signedWaiverCamp', headerName: 'Camp waiver', editable: true, width: 130, cellEditor: 'agCheckboxCellEditor', cellRenderer: 'agCheckboxCellRenderer' },

    // Emergency contact
    { field: 'firstName_emergency', headerName: 'Emerg. first name', editable: true, width: 150 },
    { field: 'lastName_emergency', headerName: 'Emerg. last name', editable: true, width: 150 },
    { field: 'email_emergency', headerName: 'Emerg. email', editable: true, width: 190 },
    { field: 'phoneNumber_emergency', headerName: 'Emerg. phone', editable: true, width: 140 },
    { field: 'isMinor', headerName: 'Minor?', editable: true, width: 100, cellEditor: 'agCheckboxCellEditor', cellRenderer: 'agCheckboxCellRenderer' },

    // Logistics
    { field: 'firstTime', headerName: 'First time?', editable: true, width: 110, cellEditor: 'agCheckboxCellEditor', cellRenderer: 'agCheckboxCellRenderer' },
    { field: 'hospitality', editable: true, width: 120, cellEditor: 'agCheckboxCellEditor', cellRenderer: 'agCheckboxCellRenderer' },
    { field: 'services', editable: true, width: 220, headerTooltip: 'Comma-separated - avoid commas within a single service name' },
    {
      field: 'allergies', editable: true, width: 200,
      headerTooltip: 'Comma-separated - e.g. dairy free, gluten free, nut free, vegetarian',
    },

    // Merchandise
    { field: 'shirtsize', headerName: 'T-shirt size', editable: true, width: 130, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: shirtSizeOptions } },

    // Financial support
    { field: 'hasFinancialSupport', headerName: 'Fin. support?', editable: true, width: 130, cellEditor: 'agCheckboxCellEditor', cellRenderer: 'agCheckboxCellRenderer' },
    { field: 'financialSupportAmount', headerName: 'Support $', editable: true, width: 110, valueFormatter: currencyFormatter },
    { field: 'financialSupportSource', headerName: 'Support source', editable: true, width: 170 },
    { field: 'servingOneName', headerName: 'Serving One', editable: true, width: 150 },
    { field: 'servingOneEmail', headerName: 'Serving One email', editable: true, width: 190 },

    // Notes
    { field: 'comments', editable: true, width: 220 },

    { field: 'createdAt', headerName: 'Timestamp', editable: false, width: 170, valueFormatter: dateTimeFormatter },
  ], [canRefund, shirtSizeOptions]);

  const gridContext = useMemo(
    () => ({ onCancelClick: handleCancelClick, onRefundClick: handleRefundClick, onDeleteClick: handleDeleteClick, canRefund }),
    [handleCancelClick, handleRefundClick, handleDeleteClick, canRefund],
  );

  const localityOptions = useMemo(() => {
    const localities = [...new Set(rows.map((r) => r.locality).filter(Boolean))];
    return localities.map((loc) => ({ value: loc, label: loc }));
  }, [rows]);

  const localityValue = useMemo(
    () => localityFilter.map((loc) => ({ value: loc, label: loc })),
    [localityFilter],
  );

  const filteredRows = useMemo(() => {
    let result = rows;
    if (localityFilter.length > 0) {
      result = result.filter((r) => localityFilter.includes(r.locality));
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((r) => SEARCH_FIELDS.some((field) => String(r[field] || '').toLowerCase().includes(q)));
    }
    return result;
  }, [rows, localityFilter, searchQuery]);

  return (
    <Box sx={{ height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Double-click a cell to edit - changes save immediately. Amount paid/canceled/refunded are reference-only and
        change only via the Cancel/Refund buttons, never by editing the cell.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ minWidth: 260 }}>
          <TextField
            select fullWidth size="small" label="Editing conference"
            value={viewingId || ''}
            onChange={(e) => setViewingId(e.target.value)}
          >
            {viewOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.label || o.id}</MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ minWidth: 260, flex: 1, maxWidth: 400 }}>
          <Select
            value={localityValue}
            isMulti
            name="locality"
            options={localityOptions}
            className="basic-multi-select"
            classNamePrefix="select"
            onChange={(values) => setLocalityFilter(values ? values.map((v) => v.value) : [])}
            placeholder="Filter by locality"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>
        <Box sx={{ minWidth: 220, flex: 1, maxWidth: 320 }}>
          <TextField
            fullWidth size="small" label="Search"
            placeholder="Name, email, phone, school, city"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>
        <Button variant="outlined" size="small" onClick={handleExportCsv} disabled={!gridApi}>
          Export CSV
        </Button>
        {canBulkUpload && viewingId && (
          <BulkUploadDialog
            conferenceId={currentOption?.mode === 'legacy' ? null : viewingId}
            legacyCollection={currentOption?.mode === 'legacy' ? viewingId : null}
            onUploaded={() => loadRows(false)}
          />
        )}
      </Box>
      {message && <Alert severity={message.type} sx={{ mb: 1 }}>{message.text}</Alert>}
      <Box className="ag-theme-quartz" sx={{ width: '100%', flex: 1 }}>
        <AgGridReact
          rowData={filteredRows}
          loading={loading}
          columnDefs={colDefs}
          theme={themeQuartz}
          onCellValueChanged={handleCellValueChanged}
          getRowId={(params) => params.data.id}
          context={gridContext}
          onGridReady={(params) => setGridApi(params.api)}
        />
      </Box>

      <Dialog open={!!refundTarget} onClose={() => (refundBusy ? null : setRefundTarget(null))}>
        <DialogTitle>Refund {refundTarget?.firstName} {refundTarget?.lastName}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This issues a real PayPal refund against their original payment. They paid{' '}
            {Number.isFinite(Number(refundTarget?.amountPaid)) ? `$${Number(refundTarget.amountPaid).toFixed(2)}` : 'an unknown amount'}.
            This cannot be undone from this tool.
          </DialogContentText>
          <TextField
            autoFocus fullWidth label="Refund amount (USD)" type="number"
            value={refundAmountInput} onChange={(e) => setRefundAmountInput(e.target.value)}
            disabled={refundBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundTarget(null)} disabled={refundBusy}>Cancel</Button>
          <Button
            color="error" variant="contained" onClick={confirmRefund}
            disabled={refundBusy || !(Number(refundAmountInput) > 0)}
          >
            {refundBusy ? 'Processing...' : 'Confirm Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => (deleteBusy ? null : setDeleteTarget(null))}>
        <DialogTitle>Delete {deleteTarget?.firstName} {deleteTarget?.lastName}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This permanently deletes their registration record. This cannot be undone.
          </DialogContentText>
          {deleteTarget && deleteTarget.amountPaid > 0 && !deleteTarget.refunded && (
            <Alert severity="warning">
              This participant paid ${Number(deleteTarget.amountPaid).toFixed(2)} and hasn't been refunded. Deleting removes
              the record needed to process a refund later - consider refunding first if that's still needed.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteBusy}>
            {deleteBusy ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
